import { useState } from 'react';
import { Button, Divider, makeStyles } from '@fluentui/react-components';
import { DocumentAddRegular, EyeRegular } from '@fluentui/react-icons';
import { Column, PortalSlotContent, Row } from '../../../common';
import { Case, Invoice, UserAccount } from '../../../../data/types';
import { CaseInvoiceTable } from './CaseInvoicesTable';
import { useCaseServicesContext } from '../Cases/CaseServicesContext';
import { appTokens } from '../../../common/Styles/appTokens';
import { CreateInvoiceDialog } from './CreateInvoiceDialog';
import { useAccess } from '../../../../auth/useAccess';
import { useAccountState } from '../../../../redux/reducers/accountReducer';
import { LinkButton } from '../../../controls/LinkButton';

const useStyles = makeStyles({
    addBtn: {
        fontSize: '13px',
        paddingBlockStart: '0',
        paddingBlockEnd: '0',
        ':focus': {
            outline: 'none',
            boxShadow: 'none',
        },
    },
});

export type CaseInvoicesListPageProps = {
    onViewInvoicePDF?: (invoice: Invoice) => void;
};

export const CaseInvoicesListPage = (props: CaseInvoicesListPageProps) => {
    const { onViewInvoicePDF } = props;

    const { caseForm, invoiceService } = useCaseServicesContext()!;
    const caseObj = caseForm.values as Case;

    //Hooks
    const { primaryAccount } = useAccountState();
    const { user } = (primaryAccount as UserAccount) ?? {};
    const access = useAccess(user);

    //State
    const [selectedInvoices, setSelectedInvoices] = useState<Invoice[]>([]);
    const [createDialogOpen, setCreateDialogOpen] = useState<boolean>(false);

    //Styles
    const styles = useStyles();

    //Access
    const canCreateInvoices = () => {
        return access.invoices.canCreateInvoices(caseObj.caseId);
    };

    //Logic
    const onDeleteInvoices = async (invoices: Invoice[]) => {
        const invoiceIds = invoices.map((inv) => inv.invoiceId as string);
        await invoiceService.deleteInvoicesAsync(invoiceIds, true);
    };
    const onSelectionChanged = (invoices: Invoice[]) => {
        setSelectedInvoices(invoices);
    };
    const onViewPDF = (invoice: Invoice) => {
        if (onViewInvoicePDF) {
            onViewInvoicePDF(invoice);
        }
    };
    const sortInvoices = (a: Invoice, b: Invoice) => {
        const aDate = new Date(a.invoiceDate || 0).valueOf();
        const bDate = new Date(b.invoiceDate || 0).valueOf();
        return aDate - bDate;
    };
    const onMarkInvoicePaid = async (invoice: Invoice) => {
        await invoiceService.markInvoicePaidAsync(invoice.invoiceId as string);
    };
    const onMarkInvoicesPaid = async (invoices: Invoice[]) => {
        await invoiceService.markInvoicesPaidAsync(invoices.map((inv) => inv.invoiceId as string));
    };
    const onCreateInvoiceClick = () => {
        setCreateDialogOpen(true);
    };
    const onInvoicesCreated = (invoices: Invoice[]) => {
        if (invoices.length > 0 && onViewInvoicePDF) {
            onViewInvoicePDF(invoices[0]);
        }
    };

    return (
        <>
            <PortalSlotContent name='invoicesPageHeader'>
                <Row gap='medium'>
                    {selectedInvoices.length === 1 && (
                        <>
                            <Button
                                icon={<EyeRegular />}
                                onClick={() => onViewPDF(selectedInvoices[0])}
                                data-testid='case-invoices-view-pdf-button'
                            >
                                View PDF
                            </Button>
                            <Divider vertical />
                        </>
                    )}
                    {canCreateInvoices() && (
                        <Button
                            icon={<DocumentAddRegular />}
                            onClick={onCreateInvoiceClick}
                            data-testid='case-invoices-create-invoice-button'
                        >
                            Create Invoice
                        </Button>
                    )}
                </Row>
            </PortalSlotContent>
            <Column scroll>
                <CaseInvoiceTable
                    bodyHeight={appTokens.pageHeight('16.75rem')}
                    invoices={[...invoiceService.invoices].sort(sortInvoices)}
                    onSelectionChanged={onSelectionChanged}
                    onDeleteInvoices={onDeleteInvoices}
                    onMarkInvoicePaid={onMarkInvoicePaid}
                    onMarkInvoicesPaid={onMarkInvoicesPaid}
                    onDoubleClickItem={onViewPDF}
                />
            </Column>
            <CreateInvoiceDialog
                open={createDialogOpen}
                onOpenChange={setCreateDialogOpen}
                onInvoicesCreated={onInvoicesCreated}
            />
        </>
    );
};
